/**
 * Build cart item payload from hotel price-check response.
 * Used by RoomExplorerSelection and ConciergeHotelRates so add-to-cart stays consistent.
 * @param {Object} priceCheckResponse - API response from price-check-for-booking
 * @param {Object} selection - { room: { roomTypeName, bedInfo, maxOccupancy, RoomTypeCode }, ratePlan: { ratePlanName, RatePlanType, RateKey, cancellationDetails, additionalDetails, rateSource?, RateSource? } }
 * @param {Object} searchCriteria - { checkInDate, checkOutDate, adults, children, currency }
 * @param {Object} options - { imageUrl?: string, sustainabilityData?: object, agentCommission?: object, rateSource?: string, rateSourceLabel?: string }
 */
import { resolveRateSourceFromPlan, RATE_SOURCE_GDS } from './rateSource';

export function buildHotelCartItemFromPriceCheck(priceCheckResponse, selection, searchCriteria, options = {}) {
  const {
    imageUrl = '/images/default-room.jpg',
    sustainabilityData = null,
    agentCommission = null
  } = options;
  // Use empty object when no sustainability data so cart/checkout never see null environmentalImpact
  const safeSustainability = sustainabilityData != null ? sustainabilityData : {};

  if (!priceCheckResponse?.HotelPriceCheckRS?.PriceCheckInfo) {
    throw new Error('Invalid price check response');
  }

  const priceCheckInfo = priceCheckResponse.HotelPriceCheckRS.PriceCheckInfo;
  const hotelInfo = priceCheckInfo.HotelInfo;
  const hotelRateInfo = priceCheckInfo.HotelRateInfo;
  const rateInfos = hotelRateInfo.RateInfos;
  const convertedRateInfo = Array.isArray(rateInfos.ConvertedRateInfo)
    ? rateInfos.ConvertedRateInfo[0]
    : rateInfos.ConvertedRateInfo;
  const roomInfo = hotelRateInfo.Rooms?.Room?.[0];
  const ratePlan = roomInfo?.RatePlans?.RatePlan?.[0];
  const ratePlanConvertedInfo = ratePlan?.ConvertedRateInfo;

  if (!convertedRateInfo) throw new Error('Missing rate information');

  const nights = Math.ceil(
    (new Date(searchCriteria.checkOutDate) - new Date(searchCriteria.checkInDate)) / (1000 * 60 * 60 * 24)
  );
  const baseRate = parseFloat(convertedRateInfo.AmountBeforeTax || 0);
  const totalRate = parseFloat(convertedRateInfo.AmountAfterTax || 0);
  const taxAmount = totalRate - baseRate;

  const taxes = [];
  if (hotelRateInfo.RateInfos?.RateInfo?.[0]?.Taxes?.TaxGroups?.TaxGroup) {
    const taxGroups = Array.isArray(hotelRateInfo.RateInfos.RateInfo[0].Taxes.TaxGroups.TaxGroup)
      ? hotelRateInfo.RateInfos.RateInfo[0].Taxes.TaxGroups.TaxGroup
      : [hotelRateInfo.RateInfos.RateInfo[0].Taxes.TaxGroups.TaxGroup];
    taxGroups.forEach((tax) => {
      taxes.push({
        name: tax.Description || 'Tax',
        amount: parseFloat(tax.Amount || 0),
        currency: tax.CurrencyCode || convertedRateInfo.CurrencyCode
      });
    });
  }

  let dailyRates = [];
  if (ratePlanConvertedInfo?.Rates?.Rate) {
    const rates = Array.isArray(ratePlanConvertedInfo.Rates.Rate)
      ? ratePlanConvertedInfo.Rates.Rate
      : [ratePlanConvertedInfo.Rates.Rate];
    dailyRates = rates.map((rate) => ({
      date: rate.StartDate,
      amount: parseFloat(rate.AmountBeforeTax || 0),
      currency: rate.CurrencyCode || convertedRateInfo.CurrencyCode
    }));
  }

  const hotelCode =
    hotelInfo.HotelCode || hotelInfo.HotelId || priceCheckResponse.HotelPriceCheckRS?.HotelRef?.HotelCode;

  const priceCheckRatePlan = roomInfo?.RatePlans?.RatePlan;
  const sabreRatePlan = Array.isArray(priceCheckRatePlan) ? priceCheckRatePlan[0] : priceCheckRatePlan;
  const rateSourceSnapshot = resolveRateSourceFromPlan({
    rateSource:
      options.rateSource ??
      selection.ratePlan?.rateSource ??
      selection.ratePlan?.RateSource ??
      sabreRatePlan?.RateSource ??
      sabreRatePlan?.rateSource ??
      RATE_SOURCE_GDS
  });
  if (options.rateSourceLabel) {
    rateSourceSnapshot.rateSourceLabel = options.rateSourceLabel;
  }

  return {
    type: 'hotel',
    title: hotelInfo.HotelName,
    image: imageUrl,
    price: totalRate,
    currency: convertedRateInfo.CurrencyCode,
    selectedDates: `${searchCriteria.checkInDate} - ${searchCriteria.checkOutDate}`,
    checkInDate: searchCriteria.checkInDate,
    checkOutDate: searchCriteria.checkOutDate,
    nights,
    maxAdults: selection.room.maxOccupancy?.adults ?? 2,
    maxChildren: selection.room.maxOccupancy?.children ?? 0,
    bedType: { type: selection.room.bedInfo || 'Standard Bed' },
    inclusions:
      (Array.isArray(selection.ratePlan.additionalDetails)
        ? selection.ratePlan.additionalDetails.join(', ')
        : selection.ratePlan.additionalDetails) || 'Standard amenities',
    cancellationPolicy: selection.ratePlan.cancellationDetails || 'Non-refundable',
    depositPolicy: 'No deposit required',
    taxes,
    fees: [],
    priceDetails: {
      baseRate,
      tax: taxAmount,
      additionalFees: 0,
      totalWithFees: totalRate
    },
    hotelId: hotelCode,
    roomTypeCode: selection.room.RoomTypeCode || roomInfo?.RoomTypeCode || 'STD',
    ratePlanName: selection.ratePlan.ratePlanName,
    ratePlanCode: selection.ratePlan.RatePlanType || ratePlan?.RatePlanCode || 'N/A',
    rateKey: selection.ratePlan.RateKey,
    rateSource: rateSourceSnapshot.rateSource,
    rateSourceLabel: rateSourceSnapshot.rateSourceLabel,
    searchParams: {
      checkIn: searchCriteria.checkInDate,
      checkOut: searchCriteria.checkOutDate,
      adults: searchCriteria.adults,
      children: searchCriteria.children,
      currency: searchCriteria.currency || 'USD'
    },
    hotelDetails: {
      name: hotelInfo.HotelName,
      rating: hotelInfo.SabreRating,
      address: hotelInfo.LocationInfo?.Address,
      contact: hotelInfo.LocationInfo?.Contact,
      chainName: hotelInfo.ChainName
    },
    rateDetails: {
      dailyRates,
      dailyRatesTotal: baseRate,
      convertedRateInfo,
      cancellationPolicy: ratePlanConvertedInfo?.CancelPenalties
    },
    sustainabilityData: safeSustainability,
    ...(agentCommission ? { agentCommission } : {})
  };
}
